import { useState, type FormEvent } from "react";
import { parseDate } from "@internationalized/date";
import type { DeliverSchemaTypes } from "@bfc-software/api-schemas";
import {
  Button,
  Checkbox,
  DatePicker,
  Search,
  Select,
  selectState as ss,
  TextInput,
  type SelectMsg,
  type SelectState,
} from "@bfc-software/pallet";
import { match } from "ts-pattern";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";

import {
  DATE_BASES,
  DEFAULT_DATE_BASIS,
  type DateBasis,
  type DeliverSearch,
  type ResolvedFilters,
} from "../-data/filters";

import styles from "./filter-bar.module.css";

export type Tenant = DeliverSchemaTypes.components["schemas"]["DashTenant"];

type Props = {
  filters: ResolvedFilters;
  /** The warehouses the caller may filter by; empty until they load. */
  tenants: ReadonlyArray<Tenant>;
  onApply: (next: Omit<DeliverSearch, "view" | "page">) => void;
  onReset: () => void;
  t: TFunction;
};

type WarehouseItem = ss.Item;

/** One real tenant as a row. */
function tenantItem(tenant: Tenant, t: TFunction): WarehouseItem {
  return {
    id: String(tenant.tenantId),
    label: t("filters.warehouseOption", {
      name: tenant.name,
      code: tenant.warehouseCode,
    }),
  };
}

/**
 * Pure. `disabled` when there is no warehouse to show (`warehouse === ""`: the
 * draft starts on the resolved tenant, so that only means none has arrived).
 * Otherwise `always`, with the selection the real tenant row if the list has it,
 * else a row naming the unknown id (a pasted link, or the list still loading).
 * That row is only the selection: it is not a list member, so opening the menu
 * shows only real tenants, and opening a link never silently moves the
 * dashboard to a different warehouse.
 */
function warehouseSelectState(a: {
  tenants: ReadonlyArray<Tenant>;
  warehouse: string;
  t: TFunction;
}): SelectState<WarehouseItem> {
  const label = ss.ariaLabel(a.t("filters.warehouse"));
  if (a.warehouse === "") {
    return ss.disabled({ label, reason: a.t("filters.noWarehouses") });
  }
  const items = a.tenants.map((tenant) => tenantItem(tenant, a.t));
  return ss.always({
    label,
    // `ready` rather than `fixed`: tenants can be `[]` while a URL tenant is set (list loading / failed)
    options: ss.ready({
      items,
      emptyLabel: a.t("filters.warehouseListEmpty"),
    }),
    selected: items.find((i) => i.id === a.warehouse) ?? {
      id: a.warehouse,
      label: a.t("filters.warehouseUnknown", { id: a.warehouse }),
    },
  });
}

type BasisItem = ss.Item; // id: DateBasis

/** Pure. A closed enum: the selected row is built from the value, so there is no lookup to miss. */
function basisSelectState(a: {
  basis: DateBasis;
  t: TFunction;
}): SelectState<BasisItem> {
  const row = (id: DateBasis): BasisItem => ({
    id,
    label: a.t(`filters.basis.${id}`),
  });
  const [first, ...rest] = DATE_BASES;
  return ss.always({
    label: ss.ariaLabel(a.t("filters.dateBasis")),
    options: ss.fixed<BasisItem>([row(first), ...rest.map(row)]),
    selected: row(a.basis),
  });
}

/**
 * Edits a draft and applies it on Search, not per keystroke: every applied
 * change is a request for the summary and the list. The parent re-keys this
 * component when the URL's filters change, so the draft never goes stale
 * against Back/Forward or a reset.
 */
export function FilterBar({ filters, tenants, onApply, onReset, t }: Props) {
  // Always a warehouse once there is one to show: the dashboard has no
  // "everything" view, so the draft starts on whatever the filters resolved to.
  const [warehouse, setWarehouse] = useState(
    filters.tenantId === undefined ? "" : String(filters.tenantId),
  );
  const [basis, setBasis] = useState<DateBasis>(filters.basis);
  const [from, setFrom] = useState(filters.from);
  const [to, setTo] = useState(filters.to);
  const [driver, setDriver] = useState(filters.driver ?? "");
  const [route, setRoute] = useState(filters.route ?? "");
  const [q, setQ] = useState(filters.q ?? "");
  const [willCall, setWillCall] = useState(filters.willCall);

  const onWarehouseMsg = (msg: SelectMsg<WarehouseItem>) =>
    match(msg)
      .with({ kind: "picked" }, ({ item }) => setWarehouse(String(item.id)))
      .otherwise(() => {}); // static lists; `picked` is the only message that acts

  const onBasisMsg = (msg: SelectMsg<BasisItem>) =>
    match(msg)
      .with({ kind: "picked" }, ({ item }) => setBasis(item.id as DateBasis)) // ids come only from DATE_BASES
      .otherwise(() => {});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply({
      ...(warehouse !== "" ? { tenantId: Number(warehouse) } : {}),
      ...(basis !== DEFAULT_DATE_BASIS ? { basis } : {}),
      from,
      to,
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(driver.trim() ? { driver: driver.trim() } : {}),
      ...(route.trim() ? { route: route.trim() } : {}),
      ...(willCall ? { willCall: true as const } : {}),
    });
  };

  return (
    <form
      className={styles.bar}
      onSubmit={submit}
      data-test-role="deliver-filters"
    >
      <div className={styles.row}>
        <Select
          state={warehouseSelectState({ tenants, warehouse, t })}
          onMsg={onWarehouseMsg}
          display={{ trigger: { minWidth: "250px" } }}
        />
        <Select
          state={basisSelectState({ basis, t })}
          onMsg={onBasisMsg}
          display={{ trigger: { minWidth: "170px" } }}
        />
        <DatePicker
          label={t("filters.from")}
          value={parseDate(from)}
          maxValue={parseDate(to)}
          onChange={(value) => value && setFrom(value.toString())}
        />
        <DatePicker
          label={t("filters.to")}
          value={parseDate(to)}
          minValue={parseDate(from)}
          onChange={(value) => value && setTo(value.toString())}
        />
        <TextInput
          name="driver"
          aria-label={t("filters.driver")}
          placeholder={t("filters.driver")}
          value={driver}
          onChange={(event) => setDriver(event.target.value)}
        />
        <TextInput
          name="route"
          aria-label={t("filters.route")}
          placeholder={t("filters.route")}
          value={route}
          onChange={(event) => setRoute(event.target.value)}
        />
      </div>
      <div className={styles.row}>
        <div className={styles.search}>
          <Search
            aria-label={t("filters.searchAria")}
            placeholder={t("filters.search")}
            value={q}
            onChange={(value) => setQ(String(value))}
            onClear={() => setQ("")}
          />
        </div>
        <Button
          kind="text-only"
          variant="primary"
          type="submit"
          text={t("filters.apply")}
        />
        <Button
          kind="text-only"
          variant="tertiary"
          type="button"
          text={t("filters.reset")}
          onPress={onReset}
        />
        <Checkbox
          name="willCall"
          value="true"
          label={t("filters.willCall")}
          isSelected={willCall}
          onChange={setWillCall}
        />
      </div>
    </form>
  );
}
